import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowDownTrayIcon, CheckIcon, ClipboardDocumentIcon, DocumentTextIcon, FolderOpenIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { dateiVorschau, originalUrl, type DateiVorschau as Vorschau, type VorschauAbschnitt } from '../../api/client';
import { datumCH } from '../../utils/laufwerk';
import Markiert from './Markiert';
import { BlobRahmen, GridCraftAnsicht, Hinweis, TabellenAnsicht } from './OriginalAnsichten';

/**
 * Vorschau einer Datei neben dem Chat und auf /datei/:id (wie die Quellenleiste in PowerCare): oben Name, Speicherort
 * (kopierbar) und Angaben, darunter das Original — live vom Laufwerk geholt, PDF springt auf die Seite der
 * Fundstelle — oder der indexierte Text mit markierten Begriffen. Nichts davon wird zwischengespeichert.
 */

type Props = { id: string; q: string; um: number | null; onClose: () => void };

function groesse(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toLocaleString('de-CH', { maximumFractionDigits: 1 })} MB`;
}

function ortVon(a: VorschauAbschnitt): string | null {
  if (a.blatt) return `Blatt ${a.blatt}`;
  if (a.seite !== null) return `Seite ${a.seite}`;
  return null;
}

/** Ordner einer Datei als Präfix („A/B/"); Pages-Pakete (A/Name.pages/Index.zip) liegen im Ordner über dem Paket. */
function ordnerVon(pfad: string): string {
  const teile = pfad.split('/');
  teile.pop();
  if (teile.length && teile[teile.length - 1].toLowerCase().endsWith('.pages')) teile.pop();
  return teile.length ? teile.join('/') + '/' : '';
}

export default function DateiVorschau({ id, q, um, onClose }: Props) {
  const ort = useLocation();
  const [v, setV] = useState<Vorschau | null>(null);
  const [abschnitte, setAbschnitte] = useState<VorschauAbschnitt[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [tab, setTab] = useState<'original' | 'text'>('text');
  const [kopiert, setKopiert] = useState(false);
  const [mehrLaedt, setMehrLaedt] = useState(false);
  // xlsx: GridCraft; „Einfache Tabelle" schaltet auf die eigene Tabellenansicht
  const [einfach, setEinfach] = useState(false);
  const fundRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let weg = false;
    setV(null); setAbschnitte([]); setFehler(null); setEinfach(false);
    dateiVorschau(id, q, um)
      .then((x) => {
        if (weg) return;
        setV(x); setAbschnitte(x.abschnitte);
        setTab(x.original.ansicht && x.original.moeglich && !x.original.zu_gross ? 'original' : 'text');
      })
      .catch((e: any) => { if (!weg) setFehler(e?.message ?? String(e)); });
    return () => { weg = true; };
  }, [id, q, um]);

  useEffect(() => {
    if (tab === 'text') fundRef.current?.scrollIntoView({ block: 'center' });
  }, [tab, v]);

  const nachladen = async (ab: number) => {
    setMehrLaedt(true);
    try {
      const x = await dateiVorschau(id, q, ab + 20);       // Backend liefert ab `um − 20`
      setAbschnitte((alt) => {
        const da = new Set(alt.map((a) => a.nr));
        return [...alt, ...x.abschnitte.filter((a) => !da.has(a.nr))].sort((a, b) => a.nr - b.nr);
      });
    } finally { setMehrLaedt(false); }
  };

  const kopieren = () => {
    if (!v) return;
    navigator.clipboard?.writeText(v.datei.pfad).then(() => { setKopiert(true); setTimeout(() => setKopiert(false), 1500); });
  };

  const fund = um !== null ? abschnitte.find((a) => a.nr === um) : undefined;
  const seite = fund?.seite ?? null;
  const von = abschnitte.length ? abschnitte[0].nr : 0;
  const bis = abschnitte.length ? abschnitte[abschnitte.length - 1].nr + 1 : 0;

  return (
    <aside data-vorschau className="flex h-full flex-col overflow-hidden border-gray-200 bg-white lg:rounded-lg lg:border lg:shadow-sm">
      <header className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-600">Datei</div>
            <div className="mt-0.5 break-words text-[16px] font-semibold leading-tight text-ps-dark-900">{v?.datei.name ?? '…'}</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-ps-dark-400 hover:bg-gray-100 hover:text-ps-dark-700" aria-label="Vorschau schliessen">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {v && (
          <>
            <button type="button" onClick={kopieren} title="Speicherort kopieren"
              className="group mt-1.5 flex w-full items-start gap-1.5 rounded px-1 py-0.5 -mx-1 text-left text-[12px] text-ps-dark-500 hover:bg-gray-50">
              <span className="min-w-0 flex-1 break-all font-mono">{v.datei.pfad}</span>
              {kopiert ? <CheckIcon className="mt-px h-4 w-4 shrink-0 text-emerald-600" />
                : <ClipboardDocumentIcon className="mt-px h-4 w-4 shrink-0 text-ps-dark-300 group-hover:text-ps-dark-600" />}
            </button>
            <div className="mt-1 text-[12px] text-ps-dark-400">
              {[v.datei.geaendert && `Geändert am ${datumCH(v.datei.geaendert)}`, v.datei.geaendert_von && `von ${v.datei.geaendert_von}`,
                v.datei.indexiert_am && `durchsuchbar seit ${datumCH(v.datei.indexiert_am)} ${new Date(v.datei.indexiert_am).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`,
                groesse(v.datei.groesse), seite !== null && `Fundstelle Seite ${seite}`].filter(Boolean).join(' · ')}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-[13px]">
                {(['original', 'text'] as const).map((t) => (
                  <button key={t} onClick={() => setTab(t)}
                    className={`rounded-md px-3 py-1 font-medium ${tab === t ? 'bg-primary-500 text-white' : 'text-ps-dark-600 hover:bg-gray-100'}`}>
                    {t === 'original' ? 'Original' : 'Text'}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              {!ort.pathname.startsWith('/dateien') && (
                <Link to={`/dateien?pfad=${encodeURIComponent(ordnerVon(v.datei.pfad))}&datei=${encodeURIComponent(id)}`}
                  className="btn-ghost flex items-center gap-1 text-[12px]" title="Im Ordner zeigen">
                  <FolderOpenIcon className="h-4 w-4" /> Im Ordner
                </Link>
              )}
              {v.original.moeglich && !v.original.zu_gross && (
                <a href={originalUrl(id, 'download')} className="btn-ghost flex items-center gap-1 text-[12px]">
                  <ArrowDownTrayIcon className="h-4 w-4" /> Herunterladen
                </a>
              )}
            </div>
          </>
        )}
      </header>

      {fehler && <div className="p-4 text-sm text-rose-700">{fehler}</div>}
      {!v && !fehler && (
        <div className="space-y-2 p-4"><div className="shimmer h-3 w-2/3 rounded" /><div className="shimmer h-3 w-5/6 rounded" /><div className="shimmer h-3 w-1/2 rounded" /></div>
      )}

      {v && tab === 'original' && (
        !v.original.moeglich ? (
          <Hinweis>Das Laufwerk ist gerade nicht verbunden — das Original lässt sich nicht öffnen. Unter „Text" steht der Inhalt aus dem Index.</Hinweis>
        ) : v.original.zu_gross ? (
          <Hinweis>Die Datei ist zu gross, um sie hier zu öffnen. Unter „Text" steht der Inhalt aus dem Index.</Hinweis>
        ) : !v.original.ansicht ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm text-ps-dark-600">
            <DocumentTextIcon className="h-8 w-8 text-ps-dark-300" />
            <p>Diese Datei kann der Browser nicht selbst anzeigen.</p>
            <div className="flex gap-2">
              <a href={originalUrl(id, 'download')} className="btn-primary flex items-center gap-1.5 text-sm"><ArrowDownTrayIcon className="h-4 w-4" /> Original herunterladen</a>
              <button onClick={() => setTab('text')} className="btn-outline text-sm">Text ansehen</button>
            </div>
          </div>
        ) : v.original.ansicht === 'gridcraft' && !einfach ? (
          <GridCraftAnsicht id={id} name={v.datei.name} onAusweichen={() => setEinfach(true)} />
        ) : v.original.ansicht === 'tabelle' || v.original.ansicht === 'gridcraft' ? (
          <>
            {v.original.ansicht === 'gridcraft' && (
              <div className="flex justify-end border-b border-gray-100 px-4 py-1.5 text-[11.5px]">
                <button onClick={() => setEinfach(false)} className="rounded px-1.5 py-0.5 text-ps-dark-500 hover:bg-gray-100">Mit GridCraft anzeigen</button>
              </div>
            )}
            <TabellenAnsicht id={id} blatt={fund?.blatt ?? null} zeile={fund?.zeile ?? null} />
          </>
        ) : (
          <BlobRahmen url={originalUrl(id, v.original.ansicht === 'umgewandelt' ? 'pdf' : 'anzeigen')} titel={v.datei.name}
            seite={v.original.ansicht === 'pdf' ? seite : null}
            info={v.original.ansicht === 'umgewandelt' ? 'Als PDF angezeigt — zum Bearbeiten das Original herunterladen.' : undefined} />
        )
      )}

      {v && tab === 'text' && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {v.datei.status !== 'indexiert' && (
            <Hinweis>Der Inhalt dieser Datei ist nicht im Index — gefunden wurde sie über ihren Namen.</Hinweis>
          )}
          {von > 0 && (
            <button disabled={mehrLaedt} onClick={() => nachladen(Math.max(0, von - 180))} className="w-full border-b border-gray-100 py-2 text-[12px] text-primary-700 hover:bg-gray-50">
              Frühere Abschnitte laden
            </button>
          )}
          <div className="space-y-1 px-4 py-3">
            {abschnitte.map((a, i) => {
              const ort = ortVon(a);
              const neuerOrt = ort && (i === 0 || ortVon(abschnitte[i - 1]) !== ort);
              const istFund = a.nr === um;
              return (
                <div key={a.nr}>
                  {neuerOrt && <div className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wider text-ps-dark-400 first:mt-0">{ort}</div>}
                  <div ref={istFund ? fundRef : undefined}
                    className={`whitespace-pre-wrap break-words rounded px-2 py-1 text-[13px] leading-relaxed text-ps-dark-700 ${a.art === 'zeile' ? 'font-mono text-[12px]' : ''} ${istFund ? 'bg-amber-50 ring-1 ring-amber-300' : ''}`}>
                    <Markiert text={a.inhalt} marken={a.marken} />
                  </div>
                </div>
              );
            })}
            {!abschnitte.length && v.datei.status === 'indexiert' && <div className="text-sm text-ps-dark-500">Diese Datei enthält keinen Text.</div>}
          </div>
          {bis < v.gesamt && (
            <button disabled={mehrLaedt} onClick={() => nachladen(bis)} className="w-full border-t border-gray-100 py-2 text-[12px] text-primary-700 hover:bg-gray-50">
              Weitere Abschnitte laden ({(v.gesamt - bis).toLocaleString('de-CH')} übrig)
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
